// src/components/Drive/AdminPanels/EditDeliveryModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography, CircularProgress,
  Switch, FormControlLabel, Box,
} from "@mui/material";
import { updateDeliveryFn } from "../../../firebase/deliveryFunctions";
import { getTransportIcon } from "../../../utils/transportIcons";

const msToLocal = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToMs = (str) => (str ? new Date(str).getTime() : null);

export function EditDeliveryModal({ delivery, open, onClose }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!delivery) return;
    setValues({
      origin:                  delivery.origin      ?? "",
      destination:             delivery.destination ?? "",
      pickupTime:              msToLocal(delivery.pickupTime),
      targetTime:              msToLocal(delivery.targetTime),
      originInstructions:      delivery.originInfo?.details?.instructions      ?? "",
      destinationInstructions: delivery.destinationInfo?.details?.instructions ?? "",
      price:                   delivery.price     ?? "",
      reward:                  delivery.reward    ?? "",
      noBilling:               delivery.noBilling ?? false,
      noReward:                delivery.noReward  ?? false,
      hide:                    delivery.hide      ?? false,
    });
  }, [delivery]);

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));
  const toggle = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.checked }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDeliveryFn(delivery.id ?? delivery.deliveryId, {
        origin:      values.origin,
        destination: values.destination,
        pickupTime:  localToMs(values.pickupTime),
        targetTime:  localToMs(values.targetTime),
        originInfo: {
          ...(delivery.originInfo ?? {}),
          details: {
            ...(delivery.originInfo?.details ?? {}),
            instructions: values.originInstructions,
          },
        },
        destinationInfo: {
          ...(delivery.destinationInfo ?? {}),
          details: {
            ...(delivery.destinationInfo?.details ?? {}),
            instructions: values.destinationInstructions,
          },
        },
        price:     values.price  !== "" ? Number(values.price)  : null,
        reward:    values.reward !== "" ? Number(values.reward) : null,
        noBilling: values.noBilling,
        noReward:  values.noReward,
        hide:      values.hide,
      });
      onClose();
    } catch (err) {
      console.error("Update failed:", err);
      alert(err.message || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  if (!delivery) return null;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getTransportIcon(delivery.deliveryTransportType, 18)}
          Muokkaa kuljetusta
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Label>Reitti</Label>
          <TextField label="Nouto-osoite"   value={values.origin      ?? ""} onChange={set("origin")}      size="small" fullWidth />
          <TextField label="Toimitusosoite" value={values.destination  ?? ""} onChange={set("destination")} size="small" fullWidth />

          <Label>Ohjeet</Label>
          <TextField label="Noutoohjeet"    value={values.originInstructions      ?? ""} onChange={set("originInstructions")}      size="small" fullWidth multiline rows={2} />
          <TextField label="Toimitusohjeet" value={values.destinationInstructions ?? ""} onChange={set("destinationInstructions")} size="small" fullWidth multiline rows={2} />

          <Label>Ajankohta</Label>
          <Stack direction="row" spacing={1.5}>
            <TextField label="Noutoaika" type="datetime-local" value={values.pickupTime ?? ""} onChange={set("pickupTime")} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Kohdeaika" type="datetime-local" value={values.targetTime ?? ""} onChange={set("targetTime")} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>

          <Label>Hinnoittelu</Label>
          <Stack direction="row" spacing={1.5}>
            <TextField label="Hinta (€)"               type="number" value={values.price  ?? ""} onChange={set("price")}  size="small" fullWidth />
            <TextField label="Kuljettajan palkkio (€)" type="number" value={values.reward ?? ""} onChange={set("reward")} size="small" fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Switch checked={values.noBilling ?? false} onChange={toggle("noBilling")} size="small" />}
              label={<Typography variant="body2">Ei laskutusta</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={values.noReward ?? false} onChange={toggle("noReward")} size="small" />}
              label={<Typography variant="body2">Ei palkkiota</Typography>}
            />
          </Stack>

          <Label>Näkyvyys</Label>
          <FormControlLabel
            control={<Switch checked={values.hide ?? false} onChange={toggle("hide")} size="small" />}
            label={
              <Typography variant="body2">
                {values.hide ? "Piilotettu kuljettajilta" : "Näkyvillä kuljettajille"}
              </Typography>
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Peruuta</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Tallennetaan..." : "Tallenna"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Label({ children }) {
  return (
    <Typography variant="caption" fontWeight={700} color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.8, mt: 0.5, mb: -1 }}>
      {children}
    </Typography>
  );
}